<template>
  <div class="similar-patents">
    <!-- 头部区域 -->
    <div class="patents-header">
      <div class="patents-header__inner">
        <h3>相似专利</h3>
        <el-button type="text" size="small" @click="back">
          <el-icon><ArrowLeft /></el-icon>
          返回分析结果
        </el-button>
      </div>
    </div>
    
    <!-- 加载状态 -->
    <div class="loading-container" v-if="isLoading">
      <el-icon class="loading-icon"><Loading /></el-icon>
      <span>正在查询相似专利...</span>
    </div>
    
    <!-- 错误信息 -->
    <div class="error-container" v-else-if="error">
      <el-alert
        title="查询失败"
        :message="error"
        type="error"
        show-icon
        :closable="false"
      />
    </div>
    
    <!-- 专利列表 -->
    <div class="patents-list" v-else>
      <div class="patent-item" v-for="patent in paginatedPatents" :key="patent.id || patent.applicationNumber">
        <div class="patent-content">
          <!-- 专利附图 -->
          <div class="patent-image">
            <img
              v-if="getPatentImage(patent)"
              :src="getPatentImage(patent)"
              :alt="patent.title"
              @error="handleImageError($event, patent)"
            />
            <div v-else class="patent-image__empty">本专利无附图</div>
          </div>
          
          <!-- 专利信息 -->
          <div class="patent-info">
            <h4 class="patent-title">{{ patent.t || '暂无专利标题' }}</h4>
            
            <!-- 专利基本信息 -->
            <div class="patent-meta">
              <div class="meta-row">
                <span class="meta-label">申请号：</span>
                <span class="meta-value">{{ patent.pn || '暂无' }}</span>
                <span class="meta-label">公开(公告)号：</span>
                <span class="meta-value">{{ patent.p || '暂无' }}</span>
                <span class="meta-label">公开(公告)日：</span>
                <span class="meta-value">{{ patent.ad || '暂无' }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">申请人：</span>
                <span class="meta-value">{{ patent.applicant || '暂无' }}</span>
                <span class="meta-label">当前权利人：</span>
                <span class="meta-value">{{ patent.currentOwner || '暂无' }}</span>
              </div>
            </div>
            
            <!-- 专利摘要 -->
            <div class="patent-abstract">
              {{ patent.abstract || '暂无专利摘要' }}
            </div>
            
            <!-- 操作按钮 -->
            <div class="patent-actions">
              <el-button type="default" size="small" @click="handleClick(patent)">查看详情</el-button>
              <el-button type="default" size="small" @click="handleRead(patent)">专利解读</el-button>
              <template v-if="!patent.isAddedToLibrary">
                <el-button 
                  type="primary" 
                  size="small" 
                  class="add-to-library-btn"
                  @click="addToLibrary(patent)"
                >
                  <el-icon><Plus /></el-icon>
                  加入库
                </el-button>
              </template>
              <template v-else>
                <el-button 
                  type="danger" 
                  text
                  size="small" 
                  class="remove-library-btn"
                  @click="removeFromLibrary(patent)"
                >
                  <el-icon><Delete /></el-icon>
                  从库中删除
                </el-button>
              </template>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div class="empty-container" v-if="paginatedPatents.length === 0">
        <el-empty description="暂无相似专利" />
      </div>
    </div>
    
    <!-- 分页控件：加载中 / 查询失败 / 无结果时不显示，避免出现「共 0 页」还能点「下一页」 -->
    <div class="pagination-container" v-if="!isLoading && !error && totalPatents > 0">
      <div class="page-buttons">
        <el-button 
          type="primary" 
          :disabled="currentPage <= 1"
          @click="handlePrevPage"
        >
          上一页
        </el-button>
        <span class="page-info">第 {{ currentPage }} 页，共 {{ Math.ceil(totalPatents / pageSize) }} 页</span>
        <el-button 
          type="primary" 
          :loading="isLoading"
          @click="handleNextPage"
        >
          下一页
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { ArrowLeft, Plus, Loading, Delete } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { patentSemanticsSearch, incopatSearch, patentPdfDrawing } from '@/api/process.js'
import { batchSavePatents, getPatentsByPath, getSelectionDetailByProject, replaceAlternativeSolutions } from '@/api/persistence'

// 定义组件接收的props
const props = defineProps({
  // 接收技术路径的description，用于查询相似专利
  techPathDescription: {
    type: String,
    default: ''
  },
  // 技术路径数据库ID：批量落库来源标记
  techPathId: {
    type: [String, Number],
    default: null
  },
  // 接收专利数据（可选）
  patents: {
    type: Array,
    default: () => []
  },
  problemDescription: {
    type: String,
    default: ''
  },
  // 项目ID：勾选记录落库用
  projectId: {
    type: [String, Number],
    default: null
  }
})

// 定义组件发射的事件
const emit = defineEmits(['back', 'update:patents'])

// 返回分析结果页面
const back = () => {
  // 返回时将专利数据传递给父组件
  emit('update:patents', patents.value)
  emit('back')
}

// 从数据库读该项目已勾选的专利（external_id -> 记录），用于合并勾选状态
const libraryPatentsMap = ref({})
const fetchSelectedPatents = async () => {
  if (!props.projectId) return
  try {
    const list = await getSelectionDetailByProject(props.projectId)
    const map = {}
    if (Array.isArray(list)) {
      list.filter(s => s.type === 2).forEach(item => {
        const key = String(item.externalId || '')
        if (key) {
          map[key] = { ...item, _dbId: Number(item.referId) }
        }
      })
    }
    libraryPatentsMap.value = map
  } catch (e) {
    console.error('查询已勾选专利失败:', e)
  }
}

// 专利先落库拿正式ID（按公开号去重）
const ensurePatentInDb = async (patent) => {
  try {
    // 统一用公开号(p)做唯一键：与解读页跳转(id=data.p)保持一致，避免同一专利重复建档
    const extId = patent.externalId || patent.p || patent.pn || null
    if (!extId) return null
    const toStr = (v) => Array.isArray(v) ? v.filter(Boolean).join('、') : (v || '')
    const cut = (s, n) => String(s || '').slice(0, n)
    // 公告日：兼容各种格式
    const toDate = (v) => {
      if (!v) return null
      let s = String(v).trim()
      if (/^\d{8}$/.test(s)) return `${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6,8)}`
      let m = s.match(/(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日/)
      if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
      m = s.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/)
      if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
      if (/^\d{9,13}$/.test(s)) {
        const d = new Date(s.length === 10 ? Number(s) * 1000 : Number(s))
        if (!isNaN(d.getTime())) {
          return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
        }
      }
      return null
    }
    const rawData = patent.rawData || {}
    if (patent.ad !== undefined && patent.ad !== null && !rawData.ad) {
      rawData.ad = patent.ad
    }
    const p = {
      externalId: cut(extId, 100),
      title: cut(patent.title || patent.t, 500),
      abstractContent: patent.abstract || patent.abstractContent || '',
      applicationNumber: cut(patent.applicationNumber || patent.pn, 100),
      publicationNumber: cut(patent.publicationNumber || patent.p, 100),
      publicationDate: toDate(patent.publicationDate || patent.ad),
      applicant: cut(toStr(patent.applicant), 255),
      patentee: cut(toStr(patent.patentee || patent.currentOwner), 255),
      imageUrl: cut(patent.imageUrl || patent.image_url || patent.image, 500),
      progress: patent.progress ?? 50,
      rawData
    }
    // 走批量落库接口（带 tech_path_id）
    const saved = await batchSavePatents(props.techPathId || 0, [p])
    // batch-save 返回数量，无法直接拿 id；用 saveOrGet 逻辑简化——返回 1 表示成功
    return saved > 0 ? p.externalId : null
  } catch (e) {
    console.error('专利落库失败:', e)
    return null
  }
}

// 更新勾选记录到 alternative_solution：增量合并（保留该项目已勾选的专利，再合并当前页面勾选的）
// removeIds：本次要移除的专利id列表（从库中删除时传入）
const persistSelection = async (selectedPatents, removeIds = []) => {
  if (!props.projectId) return
  const pid = Number(props.projectId)
  const removeSet = new Set((removeIds || []).map(Number))
  // 先读该项目已有的勾选记录（避免覆盖其他技术路径下加入的专利）
  let existing = []
  try {
    const list = await getSelectionDetailByProject(props.projectId)
    if (Array.isArray(list)) {
      existing = list
        .filter(s => s.type === 2 && s.referId && !removeSet.has(Number(s.referId)))
        .map(s => ({ projectId: pid, type: 2, referId: Number(s.referId) }))
    }
  } catch (e) {
    console.error('读取已有勾选记录失败（静默）:', e)
  }
  // 合并：已有记录 + 当前页面勾选的（去重）
  const mergedMap = new Map()
  existing.forEach(r => mergedMap.set(String(r.referId), r))
  ;(selectedPatents || [])
    .filter(p => p.isAddedToLibrary && p._dbId && !removeSet.has(Number(p._dbId)))
    .forEach(p => mergedMap.set(String(p._dbId), { projectId: pid, type: 2, referId: Number(p._dbId) }))
  try {
    await replaceAlternativeSolutions(props.projectId, [...mergedMap.values()])
  } catch (e) {
    console.error('勾选记录落库失败:', e)
  }
}

// 加入库操作：标记 + 写 alternative_solution
const addToLibrary = async (patent) => {
  const index = patents.value.findIndex(p => p.pn === patent.pn || p.id === patent.id)
  if (index === -1) {
    console.error('未找到专利:', patent)
    return
  }
  try {
    // 确保专利已落库（拿到数据库ID——批量落库后需查回）
    const extKey = patent.externalId || patent.p || patent.pn || null
    if (!extKey) {
      ElMessage.warning('专利信息不完整，无法加入库')
      return
    }
    // ① 先写勾选记录（带临时 _dbId=0 占位不行，需先落库拿id）
    // 方案：先落库 external_patents，再从库中按公开号查回 id
    await batchSavePatents(props.techPathId || 0, [toBackendPatent(patent)])
    // ② 从 external_patents 按公开号查回正式ID（用 by-path 接口查该路径下所有专利匹配）
    const pathPatents = await getPatentsByPath(props.techPathId || 0)
    let dbId = null
    if (Array.isArray(pathPatents)) {
      const hit = pathPatents.find(p => String(p.externalId) === String(extKey))
      if (hit) dbId = hit.id
    }
    // ③ 更新内存标记
    patents.value[index]._dbId = dbId
    patents.value[index].isAddedToLibrary = true
    // ④ 写 alternative_solution（需要 dbId）
    if (dbId) {
      await persistSelection(patents.value)
    }
    console.log(`专利 "${patent.title || patent.t}" 已加入库`)
    ElMessage.success('已加入库')
    emit('update:patents', patents.value)
  } catch (e) {
    console.error('加入库失败:', e)
    ElMessage.error('加入库失败：' + (e.message || '请重试'))
  }
}

// 从库中删除：去掉标记 + 更新 alternative_solution（移除该专利）
const removeFromLibrary = async (patent) => {
  const index = patents.value.findIndex(p => p.pn === patent.pn || p.id === patent.id)
  if (index === -1) {
    console.error('未找到专利:', patent)
    return
  }
  try {
    const removeId = patent._dbId
    patents.value[index].isAddedToLibrary = false
    if (removeId) {
      await persistSelection(patents.value, [removeId])
    }
    console.log(`专利 "${patent.title || patent.t}" 已从库中删除`)
    ElMessage.success('已从库中删除')
    emit('update:patents', patents.value)
  } catch (e) {
    console.error('从库中删除失败:', e)
    ElMessage.error('从库中删除失败：' + (e.message || '请重试'))
  }
}

// 前端专利对象 -> 后端 ExternalPatents 字段
const toBackendPatent = (patent) => {
  const toStr = (v) => Array.isArray(v) ? v.filter(Boolean).join('、') : (v || '')
  const cut = (s, n) => String(s || '').slice(0, n)
  const toDate = (v) => {
    if (!v) return null
    const s = String(v).trim()
    const m = s.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/)
    return m ? `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` : (/^\d{8}$/.test(s) ? `${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6,8)}` : null)
  }
  const extId = patent.externalId || patent.p || patent.pn || ''
  return {
    externalId: cut(extId, 100),
    title: cut(patent.title || patent.t, 500),
    abstractContent: patent.abstract || patent.abstractContent || '',
    applicationNumber: cut(patent.applicationNumber || patent.pn, 100),
    publicationNumber: cut(patent.publicationNumber || patent.p, 100),
    publicationDate: toDate(patent.publicationDate || patent.ad),
    applicant: cut(toStr(patent.applicant), 255),
    patentee: cut(toStr(patent.patentee || patent.currentOwner), 255),
    imageUrl: cut(patent.imageUrl || patent.image_url || patent.image, 500),
    progress: patent.progress ?? 50
  }
}

// 分页相关数据
const currentPage = ref(1)
const pageSize = ref(10)

// 专利数据
const patents = ref([])
// 加载状态
const isLoading = ref(false)
// 错误信息
const error = ref('')

// 查询相似专利
// 相似专利只取「有效」专利，见下方 effectiveness 说明
// incoPat effectiveness 取值：有效=1、失效=2、审中=3、其它=0
// 想同时保留审中（申请中）专利，把这里改成 '1,3'；也可用前端环境变量 VUE_APP_PATENT_EFFECTIVENESS 覆盖
const PATENT_EFFECTIVENESS =
  (typeof process !== 'undefined' && process.env && process.env.VUE_APP_PATENT_EFFECTIVENESS) || '1'

const searchSimilarPatents = async () => {
  if (!props.techPathDescription) {
    error.value = '技术路径描述不能为空'
    return
  }
  console.log(1)
  
  try {
    isLoading.value = true
    error.value = ''
    
    // 调用相似专利查询接口
    // effectiveness：按 incoPat 手册过滤专利有效性（有效=1、失效=2、审中=3、其它=0）
    // 只取有效：失效专利多数没有首页附图（会显示「本专利无附图」），也不符合"可参考的具体专利"定位
    // rows：incoPat 默认 20、最多 100，这里显式传 20（前端只取前 20 条，拆两批各 10 个号）
    const response = await patentSemanticsSearch({
    "searchText": props.techPathDescription,
    "effectiveness": PATENT_EFFECTIVENESS,
    "rows": "20"
    })
    
    const SemanticsList = JSON.parse(response.body).result.list || []
    console.log(SemanticsList)
    
    // 拆分为两个10条数据的批次
    const batch1 = SemanticsList.slice(0, 10)
    const batch2 = SemanticsList.slice(10, 20)
    
    // 处理第一个批次
    let incoExp1 = ''
    var semanticsScoreList1 = []
    batch1.forEach(v => {
      incoExp1 +='PN='+ v.pn + ' OR '
      semanticsScoreList1.push(v.semanticsScore)
    })
    incoExp1 = incoExp1.slice(0, -4)
    console.log('Batch 1 incoExp:', incoExp1)
    
    // 处理第二个批次
    let incoExp2 = ''
    var semanticsScoreList2 = []
    batch2.forEach(v => {
      incoExp2 +='PN='+ v.pn + ' OR '
      semanticsScoreList2.push(v.semanticsScore)
    })
    incoExp2 = incoExp2.slice(0, -4)
    console.log('Batch 2 incoExp:', incoExp2)
    
    // 调用相似专利详情接口 - 第一个批次
    const requestData1 = {
      incoExp: incoExp1
    }
    const response2 = await incopatSearch(requestData1)
    console.log(response2)
    const incopatList1 = JSON.parse(response2.body).result.rows || []
    
    // 调用相似专利详情接口 - 第二个批次
    const requestData2 = {
      incoExp: incoExp2
    }
    const response3 = await incopatSearch(requestData2)
    console.log(response3)
    const incopatList2 = JSON.parse(response3.body).result.rows || []
    
    // 合并两个批次的结果
    const incopatList = [...incopatList1, ...incopatList2]
    // 合并语义评分列表
    const semanticsScoreList = [...semanticsScoreList1, ...semanticsScoreList2]
    console.log(incopatList)
    var jsonList = []
    incopatList.forEach(v => {
      var arr = {}
      arr.pn = v.an
      arr.p = v.pn
      arr.t = v['ti-cn']
      arr.a = v['in-or']
      arr.ad = v.ad
      arr.r = ''
      arr.abstract = v['ab-cn']
      arr.currentOwner = v['in-or']
      arr.applicant = v['in-first']
      jsonList.push(arr)
    })
    semanticsScoreList.forEach((v,index) => {
        if (jsonList[index]) {
          jsonList[index].r = (v * 100).toFixed(2)
        }
      })
    // 为新获取的专利添加isAddedToLibrary字段
    const newPatents = jsonList.map(patent => ({
      ...patent,
      title: patent.t, // 确保有title字段
      id: patent.pn, // 确保有id字段
      isAddedToLibrary: false
    }))
    // 调用相似专利缩略图接口
    var arr1 = []
    newPatents.forEach(patent => {
      arr1.push(patent.p)
    })
    const requestData3 = arr1.join(',')
    const response4 = await patentPdfDrawing({pn:requestData3})
    console.log(response4)
    newPatents.forEach((v,index) => {
      v.image = response4.imageUrlList[index]
    })
    // 批量落库：查询到的专利全部写入 external_patents（带 tech_path_id 来源标记）
    try {
      const backendList = newPatents.map(p => toBackendPatent(p)).filter(p => p.externalId)
      if (backendList.length > 0) {
        await batchSavePatents(props.techPathId || 0, backendList)
        console.log('相关专利已批量落库:', backendList.length, '条')
      }
    } catch (e) {
      console.error('相关专利批量落库失败（静默）:', e)
    }
    // 合并勾选状态：从 alternative_solution 读该项目已勾选的专利
    await fetchSelectedPatents()
    const libMap = libraryPatentsMap.value
    newPatents.forEach(p => {
      const key = String(p.p || p.pn || '')
      const hit = libMap[key]
      if (hit) {
        p.isAddedToLibrary = true
        p._dbId = hit._dbId
      }
    })
    patents.value = [...newPatents]


    totalPatents.value = patents.value.length

  } catch (err) {
    console.error('查询相似专利失败:', err)
    error.value = '查询相似专利失败，请稍后重试'
    patents.value = []
    totalPatents.value = 0
  } finally {
    isLoading.value = false
  }
}

// 只接受有效图片地址；无效或加载失败时返回空串，由模板显示「本专利无附图」占位
const getPatentImage = (patent) => {
  const image = typeof patent?.image === 'string' ? patent.image.trim() : ''
  return /^https?:\/\/\S+$/i.test(image) ? image : ''
}

// 图片加载失败：清空该项地址，触发重新渲染为文字占位，避免浏览器破图图标
const handleImageError = (event, patent) => {
  if (patent) patent.image = ''
}

// 组件挂载时查询相似专利
onMounted(() => {
  searchSimilarPatents()
})

// 监听techPathDescription变化，重新查询相似专利
watch(
  () => props.techPathDescription,
  (newValue, oldValue) => {
    // 只有当值真正变化且不为空时才重新查询
    if (newValue && newValue !== oldValue) {
      console.log('技术路径描述变化，重新查询相似专利:', newValue)
      searchSimilarPatents()
    }
  }
)

// 处理上一页
const handlePrevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

// 处理下一页
const handleNextPage = async () => {
  // 计算总页数
  const totalPages = Math.ceil(totalPatents.value / pageSize.value)
  
  // 当当前页数大于总页数时，请求接口
  if (currentPage.value >= totalPages) {
    await searchSimilarPatents()
  }
  
  // 无论是否请求接口，都增加当前页数
  currentPage.value++
}

// 处理页码变化
const handleCurrentChange = (val) => {
  currentPage.value = val
}

// 处理每页条数变化
const handleSizeChange = (val) => {
  pageSize.value = val
  currentPage.value = 1
}

// 计算总专利数
const totalPatents = ref(0)

// 计算分页后的专利数据
const paginatedPatents = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  const end = start + pageSize.value
  return patents.value.slice(start, end)
})
const featureId = ref('')
const handleClick = (data) => {
  console.log(data)
  var url = ''
  featureId.value = data.p
  url = `/pdf?id=${featureId.value}`;

  window.open(url, '_blank');
}
// 处理专利解读
const handleRead = (data) => {
  console.log(data)
  var url = ''
  featureId.value = data.p
  // 带上项目ID + 专利数据库ID，解读完成后可落库 ai_interpretation
  url = `/patent?id=${encodeURIComponent(featureId.value)}&problemDescription=${encodeURIComponent(props.problemDescription)}&projectId=${props.projectId || ''}&dbId=${data._dbId || ''}`;
  window.open(url, '_blank');
}
</script>

<style scoped lang="scss">
/* 正文内容最大宽度：与 Process 页 .main-content 的 1200px 保持一致 */
$content-max-width: 1200px;

.similar-patents {
  // 本组件有两处宿主，宽度行为不同：
  //  1) Process 页内嵌：父级 .main-content 是固定 1200px 的块容器，正常撑满；
  //  2) 独立路由 /similar-patents：父级 layouts/default 的 .content 是 row flex。
  // row flex 容器里 flex item 默认按内容宽度收缩（align-items:stretch 只作用于交叉轴），
  // 因此不显式给 flex:1 时，整页会被压成左侧一条窄带，灰色底只覆盖那一条，右侧整片空白。
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  background-color: #f5f7fa;
  // 原为 100vh：宿主里上方还有 sticky header，会多出约 60px 的无谓滚动
  min-height: 100%;
  padding: 0 40px 40px;
}

/* 内容列统一限宽居中，避免超宽屏下正文被拉成极长的一行 */
.loading-container,
.error-container,
.patents-list,
.pagination-container {
  max-width: $content-max-width;
  margin-left: auto;
  margin-right: auto;
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background-color: #ffffff;
  margin-top: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  .loading-icon {
    margin-right: 8px;
    animation: spin 1.5s linear infinite;
  }
  
  span {
    font-size: 14px;
    color: #666;
  }
}

/* 错误信息样式 */
.error-container {
  margin-top: 24px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 20px;
}

/* 空状态样式 */
.empty-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 头部区域 */
.patents-header {
  background-color: #ffffff;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  // 通栏白条：抵消页面左右 40px 内边距
  margin: 0 -40px;
  padding: 0 40px;

  // 白条通栏、内容限宽居中，标题与返回按钮不会在超宽屏上被拉到两端
  &__inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: $content-max-width;
    margin: 0 auto;
    padding: 24px 0;
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
  
  .el-button {
    color: #1890ff;
    font-size: 14px;
    padding: 0;
  }
}

/* 专利列表 */
.patents-list {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 专利项 */
.patent-item {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
}

.patent-content {
  display: flex;
  gap: 20px;
  padding: 20px;
}

/* 专利附图 */
.patent-image {
  width: 150px;
  height: 150px;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }

  &__empty {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
    background: #f5f7fa;
    color: #909399;
    font-size: 13px;
  }
}

/* 专利信息 */
.patent-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.patent-title {
  font-size: 16px;
  font-weight: 600;
  color: #1890ff;
  margin: 0 0 12px 0;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
}

/* 专利基本信息 */
.patent-meta {
  margin-bottom: 12px;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.meta-row {
  margin-bottom: 6px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.meta-label {
  font-weight: 500;
  margin-right: 8px;
}

.meta-value {
  margin-right: 16px;
}

/* 专利摘要 */
.patent-abstract {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* 操作按钮 */
.patent-actions {
  display: flex;
  gap: 12px;
  margin-top: auto;
}

.add-to-library-btn {
  background-color: #1890ff;
  border-color: #1890ff;
  color: #ffffff;
  
  &:hover {
    background-color: #40a9ff;
    border-color: #40a9ff;
    color: #ffffff;
  }
}

/* 分页控件 */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  background-color: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.page-buttons {
  display: flex;
  align-items: center;
  gap: 20px;
}

.page-info {
  font-size: 14px;
  color: #666;
  min-width: 120px;
  text-align: center;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .similar-patents {
    padding: 0 20px 20px;
  }
  
  .patents-header {
    margin: 0 -20px;
    padding: 0 20px;

    &__inner {
      padding: 16px 0;
    }
  }
  
  .patent-content {
    flex-direction: column;
    padding: 16px;
  }
  
  .patent-image {
    width: 100%;
    height: auto;
    margin-bottom: 16px;
  }
  
  .patent-actions {
    flex-wrap: wrap;
  }
  
  .patent-meta {
    font-size: 12px;
  }
  
  .meta-value {
    margin-right: 8px;
  }
}
</style>